import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, unwrap, type Schemas } from './client';
import { isEmployee, useTenant } from '../auth/session';
import { ensureDevUserFor } from '../pages/groups/data';

/**
 * A query scoped to the current business: its key starts with the tenant id, so switching
 * businesses never shows cached data from another one.
 */
export function useApiQuery<T>(key: readonly unknown[], fn: () => Promise<T>, options: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> = {}) {
  const tenant = useTenant();
  return useQuery<T>({ queryKey: [tenant.id, ...key], queryFn: fn, ...options });
}

/** Marks queries stale after a change, by the first part of their key ('invoices', 'reports', ...). */
export function useInvalidate() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useCallback(
    (...keys: string[]) => Promise.all(keys.map((k) => queryClient.invalidateQueries({ queryKey: [tenant.id, k] }))),
    [queryClient, tenant.id],
  );
}

export type Account = { id: string; code: string; name: string; type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense'; system_key: string | null; is_active: boolean; description?: string | null; currency?: string };
export type TenantDetails = Schemas['Tenant'];

export const useAccounts = () => useApiQuery(['accounts'], async () => (await unwrap(api.GET('/v1/accounts'))).data as unknown as Account[], { staleTime: 60_000 });
/** The business's details. An employee cannot read them (ADR 0023), so it stays empty for them. */
export function useTenantDetails() {
  const tenant = useTenant();
  return useApiQuery(['tenant'], async () => (await unwrap(api.GET('/v1/tenant'))).data as TenantDetails, { staleTime: 60_000, enabled: !isEmployee(tenant.role) });
}
export type PaymentBank = Schemas['PaymentBank'];
/** The bank accounts printed on invoices (ADR 0037): active ones, or with `all` the deactivated ones too. */
export const usePaymentBanks = (all = false) =>
  useApiQuery(['payment-banks', all], async () => (await unwrap(api.GET('/v1/payment-banks', { params: { query: { all } } }))).data as PaymentBank[], { staleTime: 60_000 });
export type ExpenseOptions = Schemas['ExpenseOptions'];
/** What the expense form offers, and the currency for an employee. Every role but read_only. */
export function useExpenseOptions(enabled = true) {
  const tenant = useTenant();
  return useApiQuery(['expense-options'], async () => {
    await ensureDevUserFor(tenant.id);
    return (await unwrap(api.GET('/v1/expense-options'))).data as ExpenseOptions;
  }, { staleTime: 60_000, enabled });
}
/** Every row of a master-data list. The API returns at most 200 per request, so page until a short page. */
async function fetchAll<T>(page: (limit: number, offset: number) => Promise<{ data?: unknown }>): Promise<T[]> {
  const out: T[] = [];
  for (let offset = 0; ; offset += 200) {
    const rows = ((await page(200, offset)).data ?? []) as T[];
    out.push(...rows);
    if (rows.length < 200) return out;
  }
}
export const useContacts = () => useApiQuery(['contacts'], () => fetchAll<Schemas['Contact']>((limit, offset) => unwrap(api.GET('/v1/contacts', { params: { query: { limit, offset } } }))));
export const useTaxRates = () => useApiQuery(['tax-rates'], () => fetchAll<Schemas['TaxRate']>((limit, offset) => unwrap(api.GET('/v1/tax-rates', { params: { query: { limit, offset } } }))));
export const useItems = () => useApiQuery(['items'], () => fetchAll<Schemas['Item']>((limit, offset) => unwrap(api.GET('/v1/items', { params: { query: { limit, offset } } }))));

/** The business's currency for display, USD until the details load. */
export function useCurrency(): string {
  const tenant = useTenant();
  const { data } = useTenantDetails();
  const options = useExpenseOptions(isEmployee(tenant.role));
  return (data as { base_currency?: string } | undefined)?.base_currency ?? options.data?.currency ?? 'USD';
}

/** The business's rate for a currency on a day (ADR 0029), or null when one has to be typed. */
export function useRateLookup(currency: string, date: string, enabled: boolean) {
  return useApiQuery(['exchange-rates', 'lookup', currency, date], async () =>
    ((await unwrap(api.GET('/v1/exchange-rates/lookup', { params: { query: { currency, date } } }))).data as { rate?: string | null } | undefined)?.rate ?? null,
  { enabled: enabled && currency !== '' && /^\d{4}-\d{2}-\d{2}$/.test(date), staleTime: 60_000 });
}
